// src/pages/auth/RegisterPage.jsx
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { Button, Input } from '@/components/common/ui'
import toast from 'react-hot-toast'

const schema = z.object({
  name:          z.string().min(2, 'Name required'),
  email:         z.string().email('Enter a valid email'),
  phone:         z.string().min(10, 'Enter a valid phone number'),
  password:      z.string().min(8, 'Min 8 characters'),
  password_confirmation: z.string(),
  role:          z.enum(['buyer', 'supplier']),
  referral_code: z.string().optional(),
}).refine(d => d.password === d.password_confirmation, {
  message: "Passwords don't match", path: ['password_confirmation'],
})

export default function RegisterPage() {
  const { register: authRegister } = useAuth()
  const navigate = useNavigate()
  const [params]  = useSearchParams()

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { role: 'buyer', referral_code: params.get('ref') || '' },
  })

  const onSubmit = async (values) => {
    try {
      const { devOtp } = await authRegister(values)
      toast.success('Account created!')
      // Pass devOtp through navigation state so OtpVerifyPage can render the
      // yellow dev banner. Only present on dev/local — null in prod/stage.
      navigate('/verify-otp', { state: { devOtp } })
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed')
    }
  }

  return (
    <div className="card p-8">
      <h1 className="font-display font-bold text-2xl mb-1">Create account</h1>
      <p className="text-sm text-muted mb-6">Join thousands of buyers and suppliers on Fabrinza</p>

      {/* Role toggle */}
      <div className="flex rounded-xl border border-border overflow-hidden mb-6">
        {['buyer', 'supplier'].map(role => (
          <label key={role} className="flex-1 cursor-pointer">
            <input type="radio" value={role} className="sr-only" {...register('role')} />
            <div className={`py-2.5 text-center text-sm font-bold transition-colors
              ${/* rely on form value via register */ ''}`}>
              {role === 'buyer' ? '🛍 I want to buy' : '🏭 I want to sell'}
            </div>
          </label>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="Full name"     placeholder="Priya Sharma"   error={errors.name?.message}  {...register('name')} />
        <Input label="Email"         type="email" placeholder="you@example.com" error={errors.email?.message}  {...register('email')} />
        <Input label="Phone"         type="tel"   placeholder="+91 98765 43210" error={errors.phone?.message}  {...register('phone')} />
        <Input label="Password"      type="password" placeholder="Min 8 characters" error={errors.password?.message} {...register('password')} />
        <Input label="Confirm password" type="password" placeholder="Re-enter password" error={errors.password_confirmation?.message} {...register('password_confirmation')} />
        {params.get('ref') && (
          <Input label="Referral code" placeholder="XXXX" {...register('referral_code')} />
        )}

        <Button type="submit" loading={isSubmitting} className="w-full justify-center py-3 mt-2">
          Create Account
        </Button>
      </form>

      <p className="text-center text-xs text-muted mt-5">
        By registering you agree to our <Link to="/pages/terms" className="text-pink hover:underline">Terms</Link> &amp; <Link to="/pages/privacy-policy" className="text-pink hover:underline">Privacy Policy</Link>
      </p>
      <p className="text-center text-sm text-muted mt-3">
        Already have an account? <Link to="/login" className="text-pink font-bold hover:underline">Sign in</Link>
      </p>
    </div>
  )
}
